import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuth } from "context/AuthContext";
import { useLanguage } from "context/LanguageContext";
import { useEffect, useState } from "react";
import { fetchClassRoomDetailsData } from "services/data";
import { fetchWisdomClassRoomUploads } from "services/wisdom/wisdomData";

const WorkShopDetails = ({ workshopId }) => {
    const { userInfo } = useAuth();
    const { language } = useLanguage();
    const [workshopDetails, setWorkshopDetails] = useState(null);
    const [totalEquip, setTotalEquip] = useState(0);
    const [totalWorkingEquip, setTotalWorkingEquip] = useState(0);
    const [totalNotWorkingEquip, setTotalNotWorkingEquip] = useState(0);
    const [showEquipments, setShowEquipments] = useState(false);
    const [uploads, setUploads] = useState([]);
    const [showUploads, setShowUploads] = useState(false);

    const API_BASE = process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:4000";

    const fileUrl = (p) => {
        if (!p) return null;
        const safePath = p.replace(/\\/g, "/");
        const fileName = safePath.split("/").pop();
        return `${API_BASE}/api/v1/files/open/${encodeURIComponent(fileName)}`;
    };

    const InfoItem = ({ label, value }) => {
        return (
            <div className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2">
                <span className="text-gray-500">
                    {label}
                </span>
                <span className="font-bold text-gray-800">
                    {value}
                </span>
            </div>
        );
    };

    useEffect(() => {
        const loadworkshopDetails = async () => {
            try {
                const [detailsResponse, uploadsResponse] = await Promise.all([
                    fetchClassRoomDetailsData(workshopId),
                    fetchWisdomClassRoomUploads(workshopId)
                ]);
                setUploads(uploadsResponse || []);
                const totalWorking =
                    detailsResponse?.details?.reduce(
                        (sum, item) => sum + (item.working || 0),
                        0
                    ) || 0;

                const totalNotWorking =
                    detailsResponse?.details?.reduce(
                        (sum, item) => sum + (item.not_working || 0),
                        0
                    ) || 0;

                const total = totalWorking + totalNotWorking;
                setTotalEquip(total);
                setTotalWorkingEquip(totalWorking);
                setTotalNotWorkingEquip(totalNotWorking);
                setWorkshopDetails(detailsResponse || {});
            } catch (error) {
                console.error("Failed to load employee data:", error);
            }
        };

        if (userInfo?.token) {
            loadworkshopDetails();
        }
    }, [userInfo]);

    const valueOrDots = (value) => value || "........................";
    return (
        <div className="grid grid-cols-1 gap-4" dir={language ? "ltr" : "rtl"}>
            {/* User Main Info */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-6">
                    {language ? 'Basic Information' : 'المعلومات الاساسية'}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {language ? 'Full Name' : 'الاسم الكامل'}
                        </label>
                        <p className="text-gray-800">{workshopDetails?.classroom?.Name}</p>
                    </div>

                    {/* Type */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {language ? 'Type' : 'النوع'}
                        </label>
                        <div className="flex items-center gap-2">
                            <p className="overflow-hidden text-gray-800">{workshopDetails?.classroom?.room_type}</p>
                        </div>
                    </div>

                    {/* Equipments */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {language ? 'no of equipments' : 'عدد الاجهزة'}
                        </label>
                        <div className="flex items-center gap-2">
                            <div className="flex flex-wrap items-center justify-end gap-3 mt-2 text-sm font-medium">
                                <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100">
                                    <span className="text-gray-600">إجمالي</span>
                                    <span className="font-bold text-gray-900">
                                        {totalEquip}
                                    </span>
                                </div>

                                <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-green-100">
                                    <span className="text-green-700">سليم</span>
                                    <span className="font-bold text-green-600">
                                        {totalWorkingEquip}
                                    </span>
                                </div>

                                <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-red-100">
                                    <span className="text-red-700">غير سليم</span>
                                    <span className="font-bold text-red-600">
                                        {totalNotWorkingEquip}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Workshop equipement details */}
            <div className="mt-6 bg-white rounded-2xl shadow-xl p-6" dir="rtl">
                <h3 className="text-xl font-bold text-gray-800">
                    البيانات المفصلة
                </h3>

                <div className="w-full mt-4" dir="rtl">
                    <button
                        type="button"
                        onClick={() => setShowEquipments(prev => !prev)}
                        className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-gray-100 text-gray-800 font-bold shadow-sm"
                    >
                        <span>تفاصيل المعدات</span>
                        <span className="text-xl">
                            {showEquipments ? "−" : "+"}
                        </span>
                    </button>

                    {showEquipments && (
                        <div className="mt-3 space-y-3">
                            {workshopDetails?.details?.map((item) => (
                                <div
                                    key={item.id}
                                    className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                                >
                                    <div className="flex items-center justify-between gap-3 mb-3">
                                        <h3 className="font-bold text-gray-900">
                                            {item.name}
                                        </h3>

                                        <span
                                            className={`px-3 py-1 rounded-full text-xs font-bold ${item.working > 0
                                                ? "bg-green-100 text-green-700"
                                                : "bg-red-100 text-red-700"
                                                }`}
                                        >
                                            {item.working > 0 ? "سليم" : "غير سليم"}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                        <InfoItem label="العدد السليم" value={item.working} />
                                        <InfoItem label="العدد غير السليم" value={item.not_working} />
                                        <InfoItem label="المكان" value={item.location || "غير محدد"} />
                                        <InfoItem label="السنة" value={item.year || "غير محدد"} />
                                        <InfoItem label="السبب" value={item.reason || "لا يوجد"} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Workshop uploaded documents */}
            <div className="mt-6 bg-white rounded-2xl shadow-xl p-6" dir="rtl">
                <h3 className="text-xl font-bold text-gray-800">
                    المرفقات
                </h3>

                <div className="w-full mt-4" dir="rtl">
                    <button
                        type="button"
                        onClick={() => setShowUploads(prev => !prev)}
                        className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-gray-100 text-gray-800 font-bold shadow-sm"
                    >
                        <span>الملفات المرفوعة</span>
                        <span className="text-xl">
                            {showUploads ? "−" : "+"}
                        </span>
                    </button>

                    {showUploads && (
                        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                            {uploads.length > 0 ? (
                                uploads.map((upload) => (
                                    <div
                                        key={upload.id}
                                        className="rounded-xl border border-gray-200 bg-white p-2 shadow-sm"
                                    >
                                        <a href={fileUrl(upload.file_path)} target="_blank" rel="noreferrer">
                                            <img
                                                src={fileUrl(upload.file_path)}
                                                alt={upload.name || "upload"}
                                                className="w-full h-32 object-cover rounded-lg"
                                            />
                                        </a>
                                    </div>
                                ))
                            ) : (
                                <p className="text-gray-500 col-span-full">لا توجد مرفقات</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
};

export default WorkShopDetails;